import { loadPriceList } from "../lib/prices";
import type { TrackedItemInput } from "../types";
import { units, normalizedMeasure } from "../types/receipt";
import { money } from "../lib/finance";
import { useEffect, useId, useMemo, useState, useRef } from "react";
import type { PriceObservation } from "../types/receipt";
import { ChevronDown, Plus, Tag, Trash2 } from "lucide-react";

export default function TrackedItemsEditor({
  items,
  onChange,
  currency,
  disabled = false,
  single = false,
  identityDisabled = false,
}: {
  items: TrackedItemInput[];
  onChange: (items: TrackedItemInput[]) => void;
  currency: string;
  disabled?: boolean;
  single?: boolean;
  identityDisabled?: boolean;
}) {
  const suggestionId = useId();
  const expandedNewItems = useRef(new Set<string>());
  const [suggestions, setSuggestions] = useState<PriceObservation[]>([]);
  useEffect(() => {
    let active = true;
    loadPriceList().then(rows => { if (active) setSuggestions(rows); }).catch(() => {});
    return () => { active = false; };
  }, []);
  // Sorted once per price list, not on every keystroke in the editor.
  const suggestionLists = useMemo(
    () => (["name", "brand", "group"] as const).map(field =>
      [field, [...new Set(suggestions.map(p => p[field]).filter(Boolean))].sort()] as const),
    [suggestions],
  );
  const patch = (id: string, key: string, value: string) =>
    onChange(items.map((i) => (i.id === id ? { ...i, [key]: value } : i)));
  return (
    <fieldset disabled={disabled} className={`tracked-editor ${single ? "tracked-editor-single" : ""}`}>
      {suggestionLists.map(([field, values]) => <datalist id={`${suggestionId}-${field}`} key={field}>{values.map(v => <option key={v} value={v} />)}</datalist>)}
      <legend className="tracked-editor-legend">
        <span className="tracked-editor-title">
          <Tag size={14} aria-hidden="true" />
          <span>Price tracking</span>
          {!single && items.length > 0 && (
            <span className="tracked-editor-badge">{items.length}</span>
          )}
        </span>
        {!single && (
          <small className="tracked-editor-sub">
            Compare unit prices across stores
          </small>
        )}
      </legend>
      {items.length > 0 && (
        <div className="tracked-editor-list">
          {items.map((item, index) => {
            if (!item.name) expandedNewItems.current.add(item.id);
            const measure = normalizedMeasure(item.measure, item.unit);
            const groupLabel =
              item.group &&
              item.group.trim().toLowerCase() !== item.name.trim().toLowerCase()
                ? item.group
                : "";
            const rateLabel =
              measure && Number(item.total) > 0
                ? `${money(Number(item.total) / measure.measure, currency)} / ${measure.unit}`
                : item.measure && item.unit
                  ? `${item.measure} ${item.unit}`
                  : "";
            const meta = [item.brand, groupLabel, rateLabel]
              .filter(Boolean)
              .join(" · ");
            return (
              <details
                className="tracked-editor-item"
                key={item.id}
                open={single || expandedNewItems.current.has(item.id) || undefined}
              >
                <summary>
                  <span className="tracked-editor-summary-copy">
                    <strong className="tracked-editor-summary-name">
                      {item.name || `Item ${index + 1}`}
                    </strong>
                    <small className="tracked-editor-summary-meta">
                      {meta || (item.name ? "Tap to edit details" : "Enter item details")}
                    </small>
                  </span>
                  <span className="tracked-editor-summary-side">
                    <span className={`tracked-editor-summary-price ${item.total ? "" : "is-empty"}`}>
                      <span className="sr-only"> · </span>
                      {item.total ? money(Number(item.total), currency) : "Set price"}
                    </span>
                    {!single && (
                      <ChevronDown size={16} className="tracked-editor-chevron" aria-hidden="true" />
                    )}
                  </span>
                </summary>
                <div className="tracked-editor-body">
                  <label>
                    Product name
                    <input
                      required
                      disabled={identityDisabled}
                      value={item.name}
                      list={`${suggestionId}-name`}
                      placeholder="e.g. Full cream milk"
                      maxLength={160}
                      onChange={(e) => patch(item.id, "name", e.target.value)}
                    />
                  </label>
                  <div className="tracked-editor-grid-2">
                    <label>
                      Brand / variant
                      <input
                        disabled={identityDisabled}
                        value={item.brand}
                        list={`${suggestionId}-brand`}
                        placeholder="Optional"
                        maxLength={160}
                        onChange={(e) => patch(item.id, "brand", e.target.value)}
                      />
                    </label>
                    <label>
                      <span>
                        Comparison group <small>Leave blank to compare this product</small>
                      </span>
                      <input
                        disabled={identityDisabled}
                        value={item.group}
                        list={`${suggestionId}-group`}
                        placeholder={item.name || "Same as product"}
                        maxLength={80}
                        onChange={(e) => patch(item.id, "group", e.target.value)}
                      />
                    </label>
                  </div>
                  <div className="tracked-editor-pricing-grid">
                    <label>
                      Item total ({currency})
                      <input
                        required
                        inputMode="decimal"
                        placeholder="0.00"
                        value={item.total}
                        onChange={(e) => patch(item.id, "total", e.target.value)}
                      />
                    </label>
                    <label>
                      Total quantity bought
                      <input
                        inputMode="decimal"
                        placeholder="e.g. 1"
                        value={item.measure}
                        onChange={(e) => patch(item.id, "measure", e.target.value)}
                      />
                    </label>
                    <label>
                      Unit
                      <select
                        aria-label="Unit"
                        value={item.unit}
                        onChange={(e) => patch(item.id, "unit", e.target.value)}
                      >
                        {units.map((u) => (
                          <option key={u} value={u}>
                            {u || "Not specified"}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  {measure && Number(item.total) > 0 && (
                    <p className="unit-preview">
                      <span>
                        {money(Number(item.total), currency)} ÷ {measure.measure} {measure.unit} =
                      </span>{" "}
                      <strong>
                        {money(Number(item.total) / measure.measure, currency)} / {measure.unit}
                      </strong>
                    </p>
                  )}
                  <div className="tracked-editor-item-footer">
                    <details
                      className="tracked-editor-more"
                      open={Boolean(item.pack || item.notes) || undefined}
                    >
                      <summary>Pack size & notes</summary>
                      <div className="tracked-editor-more-body">
                        <label>
                          Pack size
                          <input
                            placeholder="e.g. 6 × 200ml"
                            value={item.pack}
                            onChange={e => patch(item.id, "pack", e.target.value)}
                          />
                        </label>
                        <label>
                          Notes
                          <textarea
                            rows={2}
                            placeholder="Optional quality or promo notes"
                            value={item.notes}
                            onChange={e => patch(item.id, "notes", e.target.value)}
                          />
                        </label>
                      </div>
                    </details>
                    {!single && (
                      <button
                        type="button"
                        className="danger-button tracked-remove-button"
                        onClick={() => onChange(items.filter((i) => i.id !== item.id))}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                        Remove item
                      </button>
                    )}
                  </div>
                </div>
              </details>
            );
          })}
        </div>
      )}
      {!single && (
        <button
          type="button"
          className="tracked-add-button"
          onClick={() =>
            onChange([
              ...items,
              {
                id: crypto.randomUUID(),
                name: "",
                brand: "",
                group: "",
                total: "",
                measure: "",
                unit: "",
                pack: "",
                notes: "",
              },
            ])
          }
        >
          <Plus size={15} aria-hidden="true" />
          Add item to track
        </button>
      )}
    </fieldset>
  );
}
